import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core'

import { ColDef, ValueGetterParams } from 'ag-grid-community'

import { FlxModalService } from '@flowx/angular-ui-toolkit'
import {
  ACTION_TYPE_UI,
  ELEMENT_TYPE_UI,
} from '@flowx/designer/modules/core/dictionary/audit.models'
import {
  DefaultActionsColumn,
  getActionsColumnLength,
} from '@flowx/designer/modules/projects/components/resources-grid/resources-grid.component'
import { ActionsCellComponent } from '@flowx/designer/shared/components/ag-grid/cell-components/actions-cell/actions-cell.component'
import { DateCellComponent } from '@flowx/designer/shared/components/ag-grid/cell-components/date-cell/date-cell.component'
import { AuditLogDialogComponent } from '@flowx/designer/shared/components/audit-log-dialog/audit-log-dialog.component'
import { AuditLogsTableComponent } from '@flowx/designer/shared/components/audit-logs-table/audit-logs-table.component'
import { AuditStatusCellComponent } from '@flowx/designer/shared/components/audit-logs-table/cells/status-cell/audit-status-cell.component'
import { DateFilterComponent } from '@flowx/designer/shared/components/audit-logs-table/filters/date-filter/date-filter.component'
import { DropdownFilterComponent } from '@flowx/designer/shared/components/audit-logs-table/filters/dropdown-filter/dropdown-filter.component'
import { TextFilterComponent } from '@flowx/designer/shared/components/audit-logs-table/filters/text-filter/text-filter.component'
import { UsersFilterComponent } from '@flowx/designer/shared/components/audit-logs-table/filters/users-filter/users-filter.component'
import {
  ActionStatusEnum,
  ActionTypeEnum,
  AuditLog,
  ComponentTypeEnum,
  ElementTypeEnum,
} from '@flowx/designer/shared/services/audit.service'

@Component({
  selector: 'app-instance-audit-logs',
  templateUrl: './instance-audit-logs.component.html',
  styleUrls: ['./instance-audit-logs.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [AuditLogsTableComponent],
})
export class InstanceAuditLogsComponent {
  #flxModalService = inject(FlxModalService)
  processInstanceUuid = input.required<string>()
  initialQuery = computed(() => ({
    component: ComponentTypeEnum.PROCESS_INSTANCE,
    contextIdentifier: this.processInstanceUuid(),
  }))

  subprocessNavigation = output<string>()

  subjectOptions = Object.keys(ElementTypeEnum).map((key) => ({
    value: key,
    label: ELEMENT_TYPE_UI[key],
  }))

  auditTableDef = computed<ColDef[]>(() => {
    const processInstanceUuid = this.processInstanceUuid()
    return [
      {
        headerName: 'Timestamp',
        field: 'timestamp',
        cellRenderer: DateCellComponent,
        filter: DateFilterComponent,
        suppressSizeToFit: true,
        minWidth: 200,
      },
      { headerName: 'User', field: 'author', filter: UsersFilterComponent, minWidth: 220 },
      {
        headerName: 'Subject',
        field: 'element',
        valueGetter: (params: ValueGetterParams) => ELEMENT_TYPE_UI[params?.data?.element],
        filter: DropdownFilterComponent,
        filterParams: { options: this.subjectOptions },
        minWidth: 150,
      },
      {
        headerName: 'Event',
        valueGetter: (params: ValueGetterParams) => ACTION_TYPE_UI[params?.data?.action],
        minWidth: 120,
      },
      {
        headerName: 'Subject Identifier',
        field: 'name',
        filter: TextFilterComponent,
        minWidth: 240,
      },
      {
        headerName: 'Status',
        field: 'status',
        cellRenderer: AuditStatusCellComponent,
        valueGetter: (params: ValueGetterParams) => params?.data,
        minWidth: 120,
      },
      {
        ...DefaultActionsColumn,
        cellRenderer: ActionsCellComponent<AuditLog>,
        width: getActionsColumnLength(2),
        cellRendererParams: (params) => ({
          actions: [
            ...(processInstanceUuid === params.data?.parentInstanceId &&
            params.data?.status !== ActionStatusEnum.ERROR &&
            params.data?.action === ActionTypeEnum.START_WITH_INHERIT
              ? [
                  {
                    icon: 'ph-export rotate-90',
                    tooltip: '',
                    dataTestId: 'open-subprocess-button',
                    clickAction: (auditLog: AuditLog) => this.goToSubprocess(auditLog),
                  },
                ]
              : []),
            {
              icon: 'ph-eye',
              tooltip: '',
              dataTestId: 'view-details-button',
              clickAction: (auditLog: AuditLog) => this.openDetailsModal(auditLog),
            },
          ],
        }),
      },
    ]
  })

  goToSubprocess(log: AuditLog): void {
    this.subprocessNavigation.emit(log.contextIdentifier)
  }

  openDetailsModal(auditLog: AuditLog): void {
    this.#flxModalService.show(
      AuditLogDialogComponent,
      {
        closeOnBackdropClick: true,
        backdropClass: 'overlay-backdrop',
      },
      { auditLog }
    )
  }
}
